<template>
  <span
    ref="mirror"
    :class="[props.inputAttrs.class, DS_TOKEN_INPUT_CLASSES.Mirror]"
    aria-hidden="true"
  />
  <textarea
    :ref="setTextarea"
    v-bind="draftAttrs"
    :class="[props.inputAttrs.class, DS_TOKEN_INPUT_CLASSES.Draft]"
    :style="{ order: props.order }"
    rows="1"
    @input="emit('input', $event)"
    @keydown="emit('keydown', $event)"
    @keyup="emit('keyup', $event)"
    @click="emit('click', $event)"
    @select="emit('select', $event)"
    @focus="emit('focus', $event)"
    @blur="emit('blur', $event)"
  />
</template>

<script setup lang="ts">
import { computed, ref, useTemplateRef } from "vue";
import omit from "lodash/omit";
import type {
  DSBaseInputValueSlotInputAttrs,
  DSBaseInputValueSlotProps,
} from "@/src/design_system/form_elements/base_input/types/dsBaseInput";
import { DS_TOKEN_INPUT_CLASSES } from "@/src/design_system/form_elements/token_input/constants/classes";

defineOptions({ inheritAttrs: false });

const props = defineProps<{
  inputAttrs: DSBaseInputValueSlotInputAttrs;
  setInputRef: DSBaseInputValueSlotProps["setInputRef"];
  order?: number;
  ariaDescribedby?: string;
}>();

const emit = defineEmits<{
  (e: "input", event: Event): void;
  (e: "keydown", event: KeyboardEvent): void;
  (e: "keyup", event: KeyboardEvent): void;
  (e: "click", event: MouseEvent): void;
  (e: "select", event: Event): void;
  (e: "focus", event: FocusEvent): void;
  (e: "blur", event: FocusEvent): void;
}>();

const textarea = ref<HTMLTextAreaElement | null>(null);
const mirror = useTemplateRef<HTMLElement>("mirror");

const draftAttrs = computed<Record<string, unknown>>(() => {
  const attrs = omit(props.inputAttrs, ["type", "name", "required", "class"]);

  return { ...attrs, "aria-describedby": props.ariaDescribedby ?? attrs["aria-describedby"] };
});

const setTextarea = (element: unknown): void => {
  textarea.value = element as HTMLTextAreaElement | null;
  props.setInputRef(textarea.value);
};

const focus = (position?: number | "end"): void => {
  const element = textarea.value;

  element?.focus();

  if (element && position !== undefined) {
    const offset = position === "end" ? element.value.length : position;
    element.setSelectionRange(offset, offset);
  }
};

defineExpose({ textarea, mirror, focus });
</script>
